import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { store } from 'app-redux/store'
import { useActionless, useAppSelector } from 'app-redux/hooks'
import { convertCustomCardsToCardType } from 'hooks/customCard'

import { CardType, SaveStatus } from 'types/deck'
import { CustomCard } from 'services/apiTypes/customCards.types'
import { defaultCollectionV2Card } from 'app-redux/collectionV2/types'
import { SET_DECK_STATE } from 'app-redux/deck/actions/types'
import { DOUBLE_FACED_LAYOUTS, isCardRotateable } from 'types/card'
import { ImageSize } from 'app-redux/cacheables/initialState'

import useHoveredDeckCard from 'hooks/useHoveredDeckCard'

import SaveService from 'services/save.service'

import BasicCard from 'components/card/BasicCard'
import CardForm from 'components/card/cardForm/cardForm'
import InDecks from 'components/collectionV2/cardDetails/InDecks'
import RelatedCards from 'components/collectionV2/cardDetails/RelatedCards'
import CustomCardEditForm from 'components/customCards/CustomCardEditForm'
import CardInfo from 'components/card/CardInfo/cardInfo'
import Rulings from 'components/card/cardForm/Rulings'
import Prices from 'components/card/controlsAndInfo/prices'
import PhatDropdown from 'components/formElements/PhatDropdown'
import Icon from 'components/elements/Icon'
import ImageSizeControls from 'components/collectionV2/views/Image/ImageSizeControls'
import { CardDetailsTabTypes } from 'components/card/CardModal'

import styles from './staticCardContainer.module.scss'

const StaticCardContainer = () => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [saving, setSaving] = useState<SaveStatus>('saved')
  const [view, setView] = useState<CardDetailsTabTypes>('options')
  const [size, setSize] = useState<ImageSize>(2)

  const staticCard = useAppSelector(state => state.cacheables.staticCard)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const hoveredCard = useHoveredDeckCard()

  const [flipped, setFlipped] = useState(hoveredCard.flippedDefault || false)
  const [rotated, setRotated] = useState(false)
  const [oneEightyRotate, setOneEightyRotate] = useState(false)

  useEffect(() => {
    setFlipped(hoveredCard?.flippedDefault || false)
    setRotated(false)
    setOneEightyRotate(false)
  }, [hoveredCard])

  const handleUpdateCard = (card: CardType) => {
    setSaving('saving')

    return SaveService.save(card)
      .then(() => setSaving('saved'))
      .catch(() => setSaving('error'))
  }

  const handleUpdateCustomCardInCardMap = (updatedCustomCard: CustomCard) => {
    // NOTE - keeping the existing card ID here, since converting generates a new ID. We wouldn't want to do that since we'll the card has only been edited, not replaced
    const updatedCard: CardType = {
      ...convertCustomCardsToCardType(updatedCustomCard)[0],
      id: hoveredCard.id,
      qty: hoveredCard.qty,
      customCmc: hoveredCard.customCmc,
      modifier: hoveredCard.modifier,
      colorLabel: hoveredCard.colorLabel,
      categories: hoveredCard.categories,
    }
    const { cardMap } = store.getState().deck

    // NOTE - we're updating the map directly here because we've updated the CARD data - NOT any deck data.
    // This will not cause a deck edit, cannot be undone, etc.
    setDeckState({ cardMap: { ...cardMap, [hoveredCard.id]: updatedCard } })
  }

  const isFlipCard = DOUBLE_FACED_LAYOUTS.includes(hoveredCard.layout)
  const isRotatable = isCardRotateable(hoveredCard)
  const isOneEightyRotateCard = hoveredCard.layout.includes('flip')

  if (!staticCard) return null

  let sizeClass = styles.small

  if (size === 2) sizeClass = styles.medium
  if (size === 3) sizeClass = styles.large

  return (
    <div className={`${styles.container} ${sizeClass}`}>
      <div className={styles.sizeControls}>
        <ImageSizeControls imageSize={size} onChange={setSize} />
      </div>

      <div className={styles.content}>
        <div className={styles.cardContainerOutter}>
          <div
            className={`
              ${styles.cardContainer}
              ${styles.rotationContainer}
              ${rotated ? styles.rotated : ''}
              ${oneEightyRotate ? styles.oneEightyRotate : ''}
            `}>
            <BasicCard
              includeCorderOwned
              overrideCardResolutionPreference="large"
              card={hoveredCard}
              className={styles.card}
              saving={saving === 'saving'}
              flipped={flipped}
            />
          </div>

          <Prices card={hoveredCard} className={styles.prices} />

          {/* Hiding this via CSS so the location of the arrow keys doesn't change when arrowing through cards */}
          {isFlipCard && (
            <button className={styles.flipButton} onClick={() => setFlipped(!flipped)} color="violet">
              <Icon name="refresh" />
              Flip card
            </button>
          )}

          {isRotatable && (
            <button className={styles.flipButton} onClick={() => setRotated(!rotated)} color="violet">
              <Icon name="refresh" />
              Rotate card
            </button>
          )}

          {isOneEightyRotateCard && (
            <button className={styles.flipButton} onClick={() => setOneEightyRotate(!oneEightyRotate)} color="violet">
              <Icon name="refresh" />
              Flip card
            </button>
          )}
        </div>

        <div>
          <PhatDropdown
            className={styles.viewPicker}
            triggerWrapperClassName={styles.fullWidth}
            header="Select view"
            value={view}
            options={[
              {
                label: 'Card options',
                id: 'options',
                onClick: () => setView('options'),
              },
              {
                label: 'In decks',
                id: 'inDecks',
                disabled: !userId,
                hidden: !!hoveredCard?.customCard && `${hoveredCard?.customCard?.ownerId}` !== `${userId}`,
                onClick: () => setView('inDecks'),
              },
              {
                label: 'Collection records',
                id: 'collectionRecords',
                disabled: !userId,
                hidden: !!hoveredCard.customCard,
                onClick: () => setView('collectionRecords'),
              },
              {
                label: 'Custom card details',
                id: 'customCardDetails',
                hidden: !hoveredCard?.customCard?.id || `${hoveredCard?.customCard?.ownerId}` !== `${userId}`,
                onClick: () => setView('customCardDetails'),
              },
              {
                label: 'Card info',
                id: 'info',
                onClick: () => setView('info'),
              },
              {
                label: 'Rulings',
                id: 'rulings',
                hidden: !!hoveredCard?.customCard,
                onClick: () => setView('rulings'),
              },
            ]}
          />

          {view === 'options' && (
            <CardForm card={hoveredCard} onCardUpdated={handleUpdateCard} disabled={!hoveredCard.id} />
          )}
          {view === 'inDecks' && <InDecks card={hoveredCard} />}
          {view === 'collectionRecords' && (
            <RelatedCards collectionCard={{ ...defaultCollectionV2Card, card: hoveredCard }} />
          )}
          {view === 'customCardDetails' && (
            <CustomCardEditForm card={hoveredCard} onCardEdited={handleUpdateCustomCardInCardMap} />
          )}
          {view === 'info' && <CardInfo card={hoveredCard} />}
          {view === 'rulings' && <Rulings card={hoveredCard} />}
        </div>
      </div>
    </div>
  )
}

export default StaticCardContainer
